import { Box, Heading, Image } from "@chakra-ui/react"
import { byId, isNoOrDisabledByDependency, resolveLabel } from "@esgt/helpers"
import { calculateKPI } from "@esgt/method-structure"
import type {
  AnswerDatapoint,
  CUID,
  KpiCategory,
  KpiDimension,
  KpiGroup,
  RepeatableAnswerTable,
  StaticReportValue,
} from "@esgt/types"
import environmentImg from "assets/images/environment.png"
import generalImg from "assets/images/general.png"
import governanceImg from "assets/images/governance.png"
import socialImg from "assets/images/social.png"
import { MethodI18nFragment } from "components/I18nText"
import { formatKpiValue, getKpiUnitLabel } from "lib/helpers/formatKpiValue"
import { useRating } from "lib/providers/RatingProvider"
import { getBreakdownRows } from "../../Ratings/Rating/KPI/CategoryKPIs/getBreakdownRows"
import { FreeVersionWatermark, Page, SectionSubHeading } from "../../Report/components"
import { BreakdownKpiTable } from "./BreakdownKpiTable"
import { ReportTable, type ReportTableColumn } from "./ReportTable"

/**
 * Decorative dimension cover image, keyed by dimension slug. Dimensions
 * without a dedicated image fall back to the general illustration so the
 * cover layout stays consistent (and never crashes on unknown slugs).
 */
const dimensionImages: Record<string, string> = {
  environment: environmentImg,
  social: socialImg,
  governance: governanceImg,
}

/** Columns for simple label + value KPI rows (no headers). */
const kpiColumns: ReportTableColumn[] = [{ align: "left", width: "45%" }, { align: "right" }]

/** Columns for answer-text rows — both left-aligned so long descriptions wrap naturally. */
const answerTextColumns: ReportTableColumn[] = [{ align: "left", width: "45%" }, { align: "left" }]

export const DimensionSection = ({ dimension, lastPage }: { dimension: KpiDimension; lastPage?: boolean }) => {
  const coverImage = dimensionImages[dimension.slug] ?? generalImg

  return (
    <>
      <Page named="vsme-dimension-cover" id={`dimension-${dimension.slug}`}>
        <Box height="297mm" display="flex" flexDirection="column">
          <Box flex="1" display="flex" alignItems="center" justifyContent="center">
            <Heading as="h2" fontWeight={300} fontSize="xxx-large" textAlign="center" minWidth="min-content">
              <MethodI18nFragment text={dimension.name} />
            </Heading>
          </Box>
          <Image src={coverImage} alt="" display="block" width="calc(100% + 32mm)" maxW="none" mx="-16mm" />
        </Box>
      </Page>
      <Page named="vsme-dimension" lastPage={lastPage}>
        <div className="dimension-running-header">
          <MethodI18nFragment text={dimension.name} />
        </div>

        {dimension.categories.map((category, index) => (
          <CategorySection key={category.id} category={category} watermark={index % 2 === 0} />
        ))}
      </Page>
    </>
  )
}

const CategorySection = ({ category, watermark }: { category: KpiCategory; watermark: boolean }) => {
  return (
    <Box mb="12mm" id={`category-${category.id}`} sx={{ breakInside: "avoid" }}>
      {watermark && <FreeVersionWatermark />}
      <SectionSubHeading mb="6mm" sx={{ breakAfter: "avoid" }}>
        <MethodI18nFragment text={category.name} />
      </SectionSubHeading>
      {category.groups.map((group, index) => (
        <GroupSection key={index} group={group} />
      ))}
    </Box>
  )
}

/**
 * Renders every KPI in a group as its own `ReportTable` (or `BreakdownKpiTable`).
 *
 * Each table becomes a direct child of the group wrapper, marked with either
 * `data-breakdown` or `data-kpi`. This flat sibling structure lets a single CSS
 * rule bold any `data-kpi` element that follows a `data-breakdown` element —
 * these are typically the "total" rows summarising the breakdown above.
 */
const GroupSection = ({ group }: { group: KpiGroup }) => {
  const { ratingState, ratingProfileConfig, method } = useRating()

  if (!ratingState || !method) return null

  // Build an answerValues view that excludes disabled questions so that
  // stale selections (e.g. a previously chosen counting method) don't
  // leak into unit resolution when the question is hidden.
  const questionsById = byId(method.config.questions)
  const activeAnswerValues = Object.fromEntries(
    Object.entries(ratingState.answerValues).filter(
      ([scope]) => !isNoOrDisabledByDependency(scope as CUID, questionsById, ratingState.answerValues),
    ),
  )

  // Check whether the group has any visible content. Breakdown KPIs without
  // data and without a fallback config are skipped in the report, so when
  // every KPI in the group falls into that bucket we hide the whole group
  // (including its name header) to avoid orphaned section labels.
  const hasRepeatableData =
    !!group.repeatableAnswerTable && !!activeAnswerValues[group.repeatableAnswerTable.questionId]

  const hasVisibleContent =
    group.kpiIds.some((id) => {
      const kpi = method.kpisById[id]
      if (!kpi?.breakdown) return true // non-breakdown KPIs always render
      return getBreakdownRows(ratingState.datapoints, kpi.breakdown) !== null
    }) ||
    !!group.staticValues?.length ||
    !!group.answerDatapoints?.length ||
    hasRepeatableData

  if (!hasVisibleContent) return null

  const hasBreakdown = group.kpiIds.some((id) => method.kpisById[id]?.breakdown)

  return (
    <Box
      w="100%"
      sx={{
        breakInside: "avoid",
        ...(hasBreakdown ? { "& > [data-breakdown] ~ [data-kpi]": { fontWeight: 700 } } : undefined),
      }}
    >
      {group.name && (
        <Box bg="gray.50" fontSize="10px" fontWeight={500} p="12px 4px" borderBottom="1px solid" borderColor="gray.200">
          <MethodI18nFragment text={group.name} />
        </Box>
      )}

      {group.staticValues?.map((sv, i) => (
        <StaticValueRow key={`static-${i}`} sv={sv} />
      ))}

      {group.kpiIds.map((kpiId) => {
        const kpi = method.kpisById[kpiId]

        if (kpi?.breakdown) {
          return (
            <BreakdownKpiTable
              key={kpiId}
              kpi={kpi}
              datapoints={ratingState.datapoints}
              answerValues={activeAnswerValues}
              data-breakdown=""
            />
          )
        }

        const calculated = kpi
          ? calculateKPI(
              kpi,
              ratingProfileConfig,
              ratingState.datapoints,
              ratingState.anomalies,
              ratingState.overriddenKpisById,
              method.kpisById,
            )
          : undefined

        let displayValue = "-"
        if (kpi && calculated?.value !== undefined) {
          const formatted = formatKpiValue(calculated.value.toString(), kpi.unit, activeAnswerValues)
          const label = getKpiUnitLabel(kpi.unit, activeAnswerValues)
          displayValue = label ? `${formatted} ${label}` : formatted
        }

        return (
          <ReportTable
            key={kpiId}
            data-kpi=""
            columns={kpiColumns}
            rows={[[kpi ? <MethodI18nFragment key="0" text={kpi.name} /> : kpiId, displayValue]]}
          />
        )
      })}

      {group.answerDatapoints
        ?.filter((ad) => !isNoOrDisabledByDependency(ad.questionId as CUID, questionsById, ratingState.answerValues))
        .map((ad, i) => (
          <AnswerDatapointRow key={`answer-${i}`} ad={ad} answerValues={activeAnswerValues} method={method} />
        ))}

      {group.repeatableAnswerTable &&
        !isNoOrDisabledByDependency(
          group.repeatableAnswerTable.questionId as CUID,
          questionsById,
          ratingState.answerValues,
        ) && <RepeatableAnswerTableView table={group.repeatableAnswerTable} answerValues={activeAnswerValues} />}
    </Box>
  )
}

const RepeatableAnswerTableView = ({
  table,
  answerValues,
}: {
  table: RepeatableAnswerTable
  answerValues: Record<string, { primaryAnswer?: string; [k: string]: string | null | undefined } | undefined>
}) => {
  const answers = answerValues[table.questionId]
  if (!answers) return null

  // Count groups from answer values
  const maxIndex = Object.keys(answers)
    .filter((k) => k.startsWith("input_"))
    .reduce((max, k) => Math.max(max, Number.parseInt(k.split("_")[1], 10) + 1), 0)
  const groupCount = Math.ceil(maxIndex / table.inputsPerGroup)
  if (groupCount === 0) return null

  const columns: ReportTableColumn[] = table.columns.map((col) => ({
    header: <MethodI18nFragment key={col.inputIndex} text={col.header} />,
  }))

  const rows: React.ReactNode[][] = []
  for (let g = 0; g < groupCount; g++) {
    // Skip groups with no values (empty groups)
    const hasAnyValue = table.columns.some((col) => {
      const val = answers[`input_${g * table.inputsPerGroup + col.inputIndex}`]
      return val !== undefined && val !== "" && val !== null
    })
    if (!hasAnyValue) continue

    const row = table.columns.map((col) => {
      const raw = answers[`input_${g * table.inputsPerGroup + col.inputIndex}`] ?? ""
      const text = col.selectSource ? resolveLabel(col.selectSource, raw as string) : raw
      return (
        <Box key={col.inputIndex} whiteSpace="pre-line">
          {text || "-"}
        </Box>
      )
    })
    rows.push(row)
  }

  if (rows.length === 0) return null

  return <ReportTable columns={columns} rows={rows} />
}

/**
 * Renders a single answer-datapoint as a table row inside a group.
 *
 * When `showWhen` is configured the row is only rendered if the question's
 * `primaryAnswer` matches the expected value (e.g. only show the description
 * when the user answered "yes").
 */
/** Renders a static label+value pair as a table row — always visible, no question dependency. */
const StaticValueRow = ({ sv }: { sv: StaticReportValue }) => {
  return (
    <ReportTable
      data-answer=""
      columns={answerTextColumns}
      rows={[
        [
          <MethodI18nFragment key="0" text={sv.label} />,
          <Box key="1" whiteSpace="pre-line">
            {sv.value || "-"}
          </Box>,
        ],
      ]}
    />
  )
}

const AnswerDatapointRow = ({
  ad,
  answerValues,
  method,
}: {
  ad: AnswerDatapoint
  answerValues: Record<string, { primaryAnswer?: string; [k: string]: string | null | undefined } | undefined>
  method: {
    config: {
      questions: Array<{
        id: string
        textInputs?: Array<{ selectSource?: string }>
      }>
    }
  }
}) => {
  const answer = answerValues[ad.questionId]

  // Conditional display: skip when the primary answer doesn't match.
  if (ad.showWhen && answer?.primaryAnswer !== ad.showWhen.primaryAnswer) {
    return null
  }

  const rawValue = answer?.[ad.inputKey] ?? ad.defaultValue

  // Derive selectSource from the question's text input definition.
  const inputIndex = Number.parseInt(ad.inputKey.replace("input_", ""), 10)
  const question = method.config.questions.find((q) => q.id === ad.questionId)
  const selectSource = question?.textInputs?.[inputIndex]?.selectSource

  const textValue = selectSource ? resolveLabel(selectSource, rawValue ?? "") : rawValue

  return (
    <ReportTable
      data-answer=""
      columns={answerTextColumns}
      rows={[
        [
          <MethodI18nFragment key="0" text={ad.label} />,
          <Box key="1" whiteSpace="pre-line">
            {textValue || "-"}
          </Box>,
        ],
      ]}
    />
  )
}
